import { useEffect, useMemo, useState } from 'preact/hooks';
import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';
import { BtnSecondary } from '@components/buttons';
import { LoadingIndicator } from '@components/loading-indicator';
import { SECTION_LABELS, Section } from 'shared/page-classification';

type Course = { id: UUID; title: string };

type ActivityResponse = RpxResponse<typeof rpx.communityDashboard.getChampionPageActivity>;

type SectionStat = ActivityResponse['sectionStats'][number];

type ChampionRow = ActivityResponse['championRows'][number];

type Props = {
  courses: Course[];
};

const BANNER_HIDE_AFTER_WEEKS = 8;

function todayMondayIso(): string {
  const d = new Date();
  const dayOfWeek = d.getUTCDay();
  const offset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const monday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - offset));
  return monday.toISOString().slice(0, 10);
}

function addDaysIso(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function diffInDaysIso(a: string, b: string): number {
  const da = new Date(a + 'T00:00:00Z').getTime();
  const db = new Date(b + 'T00:00:00Z').getTime();
  return Math.round((da - db) / (1000 * 60 * 60 * 24));
}

function formatWeekLabel(iso: string): string {
  const start = new Date(iso + 'T00:00:00Z');
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  const fmt = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return `${fmt(start)} – ${fmt(end)}`;
}

function formatBannerDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function buildWeekOptions(earliestWeek: string | undefined): string[] {
  const current = todayMondayIso();
  if (!earliestWeek) {
    return [current];
  }
  const weeks: string[] = [];
  let cursor = current;
  while (cursor >= earliestWeek) {
    weeks.push(cursor);
    cursor = addDaysIso(cursor, -7);
  }
  return weeks;
}

function buildCsvUrl(weekStart: string, courseId?: UUID): string {
  const params = new URLSearchParams({ weekStart });
  if (courseId) {
    params.set('courseId', courseId);
  }
  return `/csv/champion-activity.csv?${params.toString()}`;
}

export function ChampionActivity({ courses }: Props) {
  const [weekStart, setWeekStart] = useState<string>(todayMondayIso());
  const [courseId, setCourseId] = useState<UUID | undefined>(undefined);
  const [data, setData] = useState<ActivityResponse | undefined>(undefined);
  const [trackingStart, setTrackingStart] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setErrorMsg(undefined);
    Promise.all([
      rpx.communityDashboard.getChampionPageActivity({ weekStart, courseId }),
      trackingStart === undefined
        ? rpx.communityDashboard.getChampionTrackingStart()
        : Promise.resolve({ earliest: trackingStart }),
    ])
      .then(([activity, tracking]) => {
        if (cancelled) {
          return;
        }
        setData(activity);
        setTrackingStart(tracking.earliest || '');
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        setErrorMsg('Could not load Champion activity. Try again in a moment.');
        showError(err);
      })
      .finally(() => {
        if (cancelled) {
          return;
        }
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [weekStart, courseId]);

  const weekOptions = useMemo(() => buildWeekOptions(trackingStart || undefined), [trackingStart]);

  const showBanner = useMemo(() => {
    if (!trackingStart) {
      return true;
    }
    const weeksSinceStart = diffInDaysIso(todayMondayIso(), trackingStart) / 7;
    return weeksSinceStart < BANNER_HIDE_AFTER_WEEKS;
  }, [trackingStart]);

  return (
    <div class="flex flex-col gap-6">
      {showBanner && <BannerBeforeTracking trackingStart={trackingStart} />}

      <div class="flex flex-wrap items-center gap-3">
        <select
          class="inline-ruz-input"
          value={courseId || ''}
          onChange={(e) => {
            const v = (e.target as HTMLSelectElement).value;
            setCourseId(v ? (v as UUID) : undefined);
          }}
        >
          <option value="">All courses</option>
          {courses.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
            </option>
          ))}
        </select>
        <select
          class="inline-ruz-input"
          value={weekStart}
          onChange={(e) => setWeekStart((e.target as HTMLSelectElement).value)}
        >
          {weekOptions.map((w) => (
            <option key={w} value={w}>
              {formatWeekLabel(w)}
            </option>
          ))}
        </select>
        <a
          href={buildCsvUrl(weekStart, courseId)}
          class="ml-auto text-sm text-indigo-600 hover:text-indigo-800"
          download
        >
          Download CSV
        </a>
      </div>

      {isLoading && <LoadingIndicator />}

      {errorMsg && !isLoading && (
        <div class="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
          {errorMsg}
        </div>
      )}

      {!isLoading && !errorMsg && data && (
        <>
          <SummaryCards data={data} />

          {data.totalChampions === 0 ? (
            <EmptyState weekStart={weekStart} trackingStart={trackingStart} />
          ) : (
            <>
              <SectionHeatmap stats={data.sectionStats} totalChampions={data.totalChampions} />
              <ChampionTable rows={data.championRows} />
            </>
          )}
        </>
      )}
    </div>
  );
}

function BannerBeforeTracking({ trackingStart }: { trackingStart: string | undefined }) {
  if (!trackingStart) {
    return (
      <div class="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-900">
        Page-by-page tracking just turned on. Once Champions visit the site this week, this view
        will fill in.
      </div>
    );
  }
  return (
    <div class="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-900">
      Page-by-page tracking started {formatBannerDate(trackingStart)}. Weeks before that show no
      data.
    </div>
  );
}

function SummaryCards({ data }: { data: ActivityResponse }) {
  const browsedDidntEngage = data.feedToThread.visited - data.feedToThread.engaged;

  return (
    <div class="grid gap-8 sm:grid-cols-3">
      <SummaryCard
        label="Champions this week"
        value={data.totalChampions}
        sub="logged in and visited"
      />
      <SummaryCard
        label="At-risk"
        value={data.atRiskCount}
        sub="didn't click past the feed"
        tone={data.atRiskCount > 0 ? 'warning' : undefined}
      />
      <SummaryCard
        label="Browsed feed, didn't engage"
        value={browsedDidntEngage < 0 ? 0 : browsedDidntEngage}
        sub={
          data.feedToThread.visited > 0
            ? `${data.feedToThread.engaged} of ${data.feedToThread.visited} opened a thread`
            : 'no feed visits this week'
        }
      />
    </div>
  );
}

function SummaryCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: number;
  sub: string;
  tone?: 'warning';
}) {
  return (
    <div>
      <hr role="presentation" class="w-full border-t" />
      <div class="mt-6 font-medium text-sm text-gray-500">{label}</div>
      <div
        class={`mt-3 text-2xl font-semibold ${
          tone === 'warning' && value > 0 ? 'text-amber-700' : ''
        }`}
      >
        {value}
      </div>
      <div class="mt-3 text-xs text-gray-500">{sub}</div>
    </div>
  );
}

function SectionHeatmap({
  stats,
  totalChampions,
}: {
  stats: SectionStat[];
  totalChampions: number;
}) {
  if (stats.length === 0) {
    return undefined;
  }
  const maxChampions = Math.max(...stats.map((s) => s.uniqueChampions), 1);

  return (
    <div>
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Where Champions went</h3>
      <div class="border rounded-lg overflow-hidden">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 text-gray-500">
            <tr>
              <th class="text-left px-4 py-2 font-medium">Section</th>
              <th class="text-left px-4 py-2 font-medium">Champions</th>
              <th class="text-right px-4 py-2 font-medium">Visits</th>
              <th class="text-right px-4 py-2 font-medium">Engaged → next</th>
            </tr>
          </thead>
          <tbody>
            {stats.map((s) => (
              <SectionRow
                key={s.section}
                stat={s}
                maxChampions={maxChampions}
                totalChampions={totalChampions}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SectionRow({
  stat,
  maxChampions,
  totalChampions,
}: {
  stat: SectionStat;
  maxChampions: number;
  totalChampions: number;
}) {
  const widthPct = Math.round((stat.uniqueChampions / maxChampions) * 100);
  const sharePct =
    totalChampions > 0 ? Math.round((stat.uniqueChampions / totalChampions) * 100) : 0;

  return (
    <tr class="border-t">
      <td class="px-4 py-3 font-medium text-gray-900">
        {SECTION_LABELS[stat.section] || stat.section}
      </td>
      <td class="px-4 py-3">
        <div class="flex items-center gap-2">
          <div class="bg-gray-100 rounded h-2 w-32 overflow-hidden">
            <div class="bg-indigo-500 h-full" style={`width:${widthPct}%`} />
          </div>
          <span class="text-gray-700 tabular-nums">
            {stat.uniqueChampions}
            <span class="text-gray-400 ml-1">({sharePct}%)</span>
          </span>
        </div>
      </td>
      <td class="px-4 py-3 text-right text-gray-700 tabular-nums">{stat.totalVisits}</td>
      <td class="px-4 py-3 text-right text-gray-500 text-xs">
        {stat.engagedNext ? (
          <span>
            <span class="text-gray-700 font-medium">
              {stat.engagedNext.count}/{stat.engagedNext.total}
            </span>{' '}
            {stat.engagedNext.label}
          </span>
        ) : (
          <span class="text-gray-300">—</span>
        )}
      </td>
    </tr>
  );
}

function ChampionTable({ rows }: { rows: ChampionRow[] }) {
  const [showAll, setShowAll] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState<string | undefined>(undefined);

  const visible = showAll ? rows : rows.slice(0, 25);

  function handleCopy(email: string) {
    navigator.clipboard
      .writeText(email)
      .then(() => {
        setCopiedEmail(email);
        setTimeout(() => setCopiedEmail(undefined), 1500);
      })
      .catch(() => undefined);
  }

  return (
    <div>
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Per-Champion activity</h3>
      <div class="border rounded-lg overflow-hidden">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 text-gray-500">
            <tr>
              <th class="text-left px-4 py-2 font-medium">Champion</th>
              <th class="text-left px-4 py-2 font-medium">Top section</th>
              <th class="text-right px-4 py-2 font-medium">Visits</th>
              <th class="text-left px-4 py-2 font-medium">Notes</th>
              <th class="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.userId} class="border-t">
                <td class="px-4 py-3">
                  <div class="font-medium text-gray-900">{r.name}</div>
                  <div class="text-xs text-gray-500">{r.email}</div>
                </td>
                <td class="px-4 py-3 text-gray-700">
                  {SECTION_LABELS[r.topSection as Section] || r.topSection}
                  {r.topLessonTitles && r.topLessonTitles.length > 0 && (
                    <div class="text-xs text-gray-500 mt-0.5" title={r.topLessonTitles.join(' · ')}>
                      {r.topLessonTitles[0]}
                      {r.topLessonTitles.length > 1 && ` +${r.topLessonTitles.length - 1}`}
                    </div>
                  )}
                </td>
                <td class="px-4 py-3 text-right text-gray-700 tabular-nums">{r.totalVisits}</td>
                <td class="px-4 py-3 text-xs">
                  {r.isAtRisk ? (
                    <span class="bg-amber-100 text-amber-800 rounded-md px-1.5 py-0.5 font-medium">
                      At-risk
                    </span>
                  ) : (
                    <span class="text-gray-300">—</span>
                  )}
                </td>
                <td class="px-2 py-3 text-right">
                  <button
                    type="button"
                    class="text-xs text-indigo-600 hover:text-indigo-800"
                    onClick={() => handleCopy(r.email)}
                  >
                    {copiedEmail === r.email ? 'Copied' : 'Copy email'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 25 && !showAll && (
        <div class="text-center mt-6">
          <BtnSecondary onClick={() => setShowAll(true)}>
            Show all {rows.length} Champions
          </BtnSecondary>
        </div>
      )}
    </div>
  );
}

function EmptyState({
  weekStart,
  trackingStart,
}: {
  weekStart: string;
  trackingStart: string | undefined;
}) {
  if (trackingStart && weekStart < trackingStart) {
    return (
      <div class="text-sm text-gray-500 py-12 text-center border rounded-lg">
        No tracking data for this week — page-by-page tracking started{' '}
        {formatBannerDate(trackingStart)}.
      </div>
    );
  }
  return (
    <div class="text-sm text-gray-500 py-12 text-center border rounded-lg">
      No Champions visited the site this week.
    </div>
  );
}
